import axios from "axios";
import heic2any from "heic2any";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const SubCategoryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Main states
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form states
  const [title, setTitle] = useState("");
  const [images, setImages] = useState([]);
  const [singleImage, setSingleImage] = useState(null);
  const [showWebsite, setShowWebsite] = useState(false);
  const [isButtonLoading, setIsLoading] = useState(false);

  // Category states
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({ _id: "", title: "" });
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  // Image upload states
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingImages, setIsDraggingImages] = useState(false);
  const [isLoadingSingleImage, setIsLoadingSingleImage] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoadingCategories(true);
      try {
        const response = await axios.get("https://server-gs.vercel.app/all-main-category");
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to load categories");
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch product details
  useEffect(() => {
    const fetchProductDetails = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `https://server-gs.vercel.app/admin/sub-category/${id}`
        );
        const data = await response.json();

        setProduct(data);

        if (data) {
          setTitle(data.title || "");
          setImages(data.images || []);
          setSingleImage(data.singleImage || null);
          setShowWebsite(data.showWebsite || false);
          
          // Set the selected category if it exists
          if (data.categoryObjectId) {
            setSelectedCategory({ _id: data.categoryObjectId, title: data.categoryTitle });
          }
        }
      } catch (error) {
        console.error("Failed to fetch product details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProductDetails();
  }, [id]);

  // Handle category change
  const handleCategoryChange = (e) => {
    const selectedId = e.target.value;
    const category = categories.find(cat => cat._id === selectedId);
    setSelectedCategory(category || { _id: "", title: "" });
  };

  // Handle drag events for single image
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  // Handle file drop for single image
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      await handleFile(file, setIsLoadingSingleImage, setSingleImage);
      e.dataTransfer.clearData();
    }
  };

  // Handle file selection for single image
  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      await handleFile(file, setIsLoadingSingleImage, setSingleImage);
    }
  };

  // Handle file processing (HEIC conversion)
  const handleFile = async (file, setIsLoading, setImageState) => {
    setIsLoading(true);
    try {
      if (file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif")) {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });

        const convertedFile = new File(
          [convertedBlob],
          file.name.replace(/\.heic$|\.heif$/i, ".jpeg"),
          { type: "image/jpeg" }
        );

        setImageState(convertedFile);
      } else {
        setImageState(file);
      }
    } catch (error) {
      console.error("Error processing file:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("showWebsite", showWebsite);
    formData.append("categoryObjectId", selectedCategory._id); // Add category ID
    formData.append("categoryTitle", selectedCategory.title); // Add category title
    // Handle images
    if (singleImage instanceof File) {
      formData.append("singleImage", singleImage);
    } else if (product?.singleImage && !singleImage) {
      formData.append("existingSingleImage", product.singleImage);
    }

    // Handle multiple images
    if (images.length > 0) {
      images.forEach(image => {
        if (image instanceof File) {
          formData.append("images", image);
        }
      });
    } else if (product?.images) {
      product.images.forEach(image => {
        formData.append("existingImages", image);
      });
    }

    try {
      const response = await axios.put(
        `https://server-gs.vercel.app/update-sub-category/${id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      Swal.fire({
        title: "Success!",
        text: "Sub-category updated successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    } catch (error) {
      console.error("Error updating sub-category:", error);
      toast.error("Failed to update sub-category");
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!product) {
    return <p className="text-center mt-10">No sub-category found</p>;
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-10">
      <div className="w-full py-6 px-6 border-b border-gray-200 mb-10">
        <h1 className="text-2xl font-semibold font-roboto text-gray-500">
          Edit Sub-Category
        </h1>
      </div>
      
      <div className="mx-auto w-[95%]">
        <form onSubmit={handleSubmit} encType="multipart/form-data" className="bg-white p-8 rounded-lg shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              <div>
                <label className="block text-md font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  required
                />
              </div>

              <div className="w-full">
                <label className="block text-md font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  {isLoadingCategories ? (
                    <div className="flex items-center justify-center py-3 border border-gray-300 rounded-lg bg-gray-100">
                      <svg
                        className="animate-spin h-5 w-5 text-blue-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span className="ml-2">Loading categories...</span>
                    </div>
                  ) : (
                    <select
                      value={selectedCategory._id}
                      onChange={handleCategoryChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      required
                    >
                      <option value="">Select a category</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.title}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <label htmlFor="showWebsite" className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      id="showWebsite"
                      type="checkbox"
                      className="sr-only"
                      checked={showWebsite}
                      onChange={() => setShowWebsite(!showWebsite)}
                    />
                    <div className={`w-10 h-6 rounded-full shadow-inner transition-colors duration-200 ${showWebsite ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                    <div className={`dot absolute w-6 h-6 bg-white rounded-full shadow -left-1 -top-0 transition-transform duration-200 ${showWebsite ? 'transform translate-x-full' : ''}`}></div>
                  </div>
                  <div className="ml-3 text-gray-700 font-medium">
                    Show on website
                  </div>
                </label>
              </div>
            </div>

            {/* Right Column - Image Upload */}
            <div className="space-y-6">
              <div>
                <label className="block text-md font-medium text-gray-700 mb-1">
                  Main Image
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300"
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {isLoadingSingleImage ? (
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="text-gray-500">Processing image...</p>
                    </div>
                  ) : singleImage ? (
                    <div className="flex flex-col items-center">
                      {typeof singleImage === 'string' ? (
                        <img
                          src={singleImage}
                          alt="Current"
                          className="w-32 h-32 object-cover mb-4 rounded-lg"
                        />
                      ) : (
                        <img
                          src={URL.createObjectURL(singleImage)}
                          alt="Preview"
                          className="w-32 h-32 object-cover mb-4 rounded-lg"
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => setSingleImage(null)}
                        className="text-sm text-red-600 underline"
                      >
                        Remove Image
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-gray-500">
                        Drag & drop an image here or click to select
                      </p>
                      <input
                        type="file"
                        id="singleImageInput"
                        className="hidden"
                        onChange={handleFileChange}
                        accept="image/*,.heic,.heif"
                      />
                      <label
                        htmlFor="singleImageInput"
                        className="mt-2 inline-block px-4 py-2 bg-gray-100 rounded-md text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors"
                      >
                        Select Image
                      </label>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full flex justify-start mt-8">
            <button
              type="submit"
              className="bg-blue-600 w-56 text-white font-medium px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              disabled={isButtonLoading}
            >
              {isButtonLoading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating...
                </>
              ) : (
                "Update Sub-Category"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubCategoryDetails;